import {
  generateCommitment,
  parseJwt,
  Encryption,
  Signing,
  verifyJwt,
  generateClaimToken,
  generateDisbursementToken,
} from "@credify/crypto";
import { CredifyClass } from "./common";
import {
  aggregateClaims,
  camelize,
  handleAPIConfiguration,
  validateDisbursementClaimKey,
} from "./utils";
import { ClaimProviderApiFactory, CommonResponse, PublicKeyResponse } from "@credify/api-docs/apiClients/v1";
import { BNPL_ORDER_PREFIX } from "./constants/bnpl";
import {EncryptedClaimObject, GenerateDisbursementDocumentsDataResponse} from "./type";

const REQUEST_EXPIRATION_DURATION = 60 * 60;

const CUSTOM_SCOPE_PREFIX = {
  offer: "offer:",
  dop: "dop:",
  bnplOrder: "bnpl_order:",
};

export class Claims extends CredifyClass {
  generateCommitments = (claims: any): object => {
    const scopeNames = Object.keys(claims);
    const value = {};
    scopeNames.forEach((scopeName) => {
      // @ts-ignore
      value[scopeName] = generateCommitment();
    });
    return value;
  };

  /**
   * claimTokens
   * @param id: string - Organization ID hosting this server
   * @param entityId: string
   * @param claims: any
   * @example claims:
   * {
   *   "finscore-score": {
   *     "finscore-score:credit-score": 1234,
   *     "finscore-score:social-score": 10
   *   }
   * }
   *
   * @example commitments:
   * {
   *   "finscore-score": "abcdefghijk"
   * }
   */
  claimTokens = (
    id: string,
    entityId: string,
    claims: any
  ): { claimTokens: any; commitments: any } => {
    const scopeNames = Object.keys(claims);
    const commitmentsCompose = {};
    const tokens = scopeNames.map((scopeName) => {
      const { token, commitment } = generateClaimToken(
        this.signing,
        id,
        entityId,
        scopeName,
        claims
      );
      // @ts-ignore
      commitmentsCompose[scopeName] = commitment;
      return token;
    });

    return {
      claimTokens: tokens,
      commitments: commitmentsCompose,
    };
  };

  /**
   * Push claim tokens
   * This is for Market
   * @param id - Organization ID
   * @param entityId - Subject individual ID
   * @param claims
   * @returns Promise<object> - commitment values
   */
  public async push(
    id: string,
    entityId: string,
    claims: any
  ): Promise<object> {
    const { claimTokens, commitments } = this.claimTokens(id, entityId, claims);
    try {
      const configuration = handleAPIConfiguration(this.config.mode);
      const client = ClaimProviderApiFactory(configuration, "", this.apiClient.instance as any);
      client.updateAttachedClaim(entityId, claimTokens)
    } catch (error) {
      throw error;
    }
    return commitments;
  }

  /**
   * Push claim tokens
   * This is for Market
   * @param id - Organization ID
   * @param entityId - Subject individual ID
   * @param claims
   * @returns Promise<object> - commitment values
   */
  public async pushDisbursementDocuments(
    id: string,
    entityId: string,
    claims: any
  ): Promise<object> {
    if (!claims) {
      throw new Error("Compose claim is not valid.");
    }

    const invalidClaim = Object.keys(claims).find((key) => {
      return !validateDisbursementClaimKey(key);
    });
    if (invalidClaim) {
      throw new Error("Compose claim is not valid.");
    }

    return this.push(id, entityId, claims);
  }

  /**
   * Validates access token, request token, and approval token
   * and returns a payload required to proceed to the next step.
   * @param accessToken
   * @param requestToken
   * @param approvalToken
   * @returns Promise<{ publicKey: string, scopes: string[] }>
   */
  public async validateRequest(
    accessToken: string,
    requestToken: string,
    approvalToken: string
  ): Promise<{ publicKey: string; scopes: string[] }> {
    const now = Number((new Date().getTime() / 1000).toFixed(0));
    await this.validateAccessToken(accessToken);
    const requestPayload = await this.validateRequestToken(requestToken, now);
    const requiredStandardScopes: string[] =
      requestPayload.scopes.split(" ") || [];
    const grantedScopes = await this.validateApprovalToken(
      requiredStandardScopes,
      approvalToken,
      now
    );
    const encryptionPublicKey = requestPayload.encryptionPublicKey;
    return { publicKey: encryptionPublicKey, scopes: grantedScopes };
  }

  /**
   * Validates an approval token and returns boolean
   * @param accessToken
   * @returns Promise<boolean>
   */
  async validateAccessToken(accessToken: string): Promise<boolean> {
    const body = {
      token: accessToken,
      scope: "oidc_client:read_encrypted_claims",
    };
    const res = await this.apiClient.post("v1/oauth2/introspect", body);
    // @ts-ignore
    const value = res.data.data;
    // @ts-ignore
    const valid0 = value?.active === true;
    if (!valid0) {
      throw new Error("Access token is not valid.");
    }
    return true;
  }

  /**
   * Validates a request token and returns the provided JWT's payload
   * If validation fails, this function raises an error.
   * @param requestToken
   * @param currentTime
   * @returns Promise<object>
   */
  async validateRequestToken(
    requestToken: string,
    currentTime: Number
  ): Promise<any> {
    const requestJwt = parseJwt(requestToken);
    let requestPayload = camelize(requestJwt.payload);
    if (requestPayload.iat + REQUEST_EXPIRATION_DURATION <= currentTime) {
      throw new Error("This request is expired.");
    }
    if (requestPayload.iat > currentTime) {
      throw new Error("This request timestamp is invalid.");
    }

    const requesterKey = await this.apiClient.get<
      PublicKeyResponse
    >(`v1/entity/public-key?entity_id=${requestPayload.iss}`);
    const pub1 = requesterKey.data.data?.signing || "";
    if (pub1 === "") {
      throw new Error("Public key is not found.");
    }
    const s1 = new Signing();
    s1.importPublicKey(pub1);
    const valid1 = verifyJwt(requestJwt, s1);
    if (!valid1) {
      throw new Error("Signature of request token is not valid.");
    }

    // If Service Provider hasn't passed an ephemeral encryption public key in the request payload,
    // This function should use a default encryption key fetched from API.
    if (!requestPayload.encryptionPublicKey) {
      const defaultEncryptionPublicKey = requesterKey.data.data?.encryption || "";
      if (defaultEncryptionPublicKey === "") {
        throw new Error("Encryption key is not found.")
      } else {
        requestPayload = {
          ...requestPayload,
          encryptionPublicKey: defaultEncryptionPublicKey
        }
      }
    }

    return requestPayload;
  }

  /**
   * Validates approval token and returns granted scope list
   * If there is any validation error, this function raises an error
   * @param requiredStandardScopes
   * @param approvalToken
   * @param currentTime
   */
  validateApprovalToken(
    requiredStandardScopes: string[],
    approvalToken: string,
    currentTime: Number
  ) {
    const approvalJwt = parseJwt(approvalToken);
    const approvalPayload = camelize(approvalJwt.payload);
    if (approvalPayload.iat + REQUEST_EXPIRATION_DURATION <= currentTime) {
      throw new Error("This request is expired.");
    }
    if (approvalPayload.iat > currentTime) {
      throw new Error("This request timestamp is invalid.");
    }
    const grantedScopes: string[] = approvalPayload.scopes.split(" ") || [];
    const validScopes = requiredStandardScopes.every((val) => {
      if (
        val.startsWith(CUSTOM_SCOPE_PREFIX.offer) ||
        val.startsWith(CUSTOM_SCOPE_PREFIX.dop) ||
        val.startsWith(CUSTOM_SCOPE_PREFIX.bnplOrder)
      ) {
        return true;
      }
      return grantedScopes.includes(val);
    });
    if (!validScopes) {
      throw new Error("Granted scopes are not valid.");
    }
    
    return grantedScopes;
  }

  /**
   * Encrypts claim values with an ephemeral private key.
   * This is for Market.
   * @param claimValues - claim object
   * @param publicKey - pem file or Base64 URL encoded string
   * @returns Promise<any>
   */
  public async encrypt(claimValues: any, publicKey: string): Promise<EncryptedClaimObject> {
    let pubKey = publicKey;
    if (publicKey.startsWith("M")) {
      // Convert public key's format from Base64 URL to pem
      const enc = new Encryption();
      await enc.importPublicKeyInBase64Url(publicKey);
      pubKey = await enc.exportPublicKey()
    }

    // This is a new version to use JWE
    return aggregateClaims(claimValues, pubKey, this.signing);
  }

  /**
   * Encrypts claim values with an ephemeral private key.
   * This is for Market.
   * @param claimValues - claim object
   * @param publicKey - pem file or Base64 URL encoded string
   * @param providerId
   * @param entityId
   * @return Promise<GenerateDisbursementDocumentsDataResponse>
   */
  public async generateDisbursementDocumentsData(
    claimValues: any,
    publicKey: string,
    providerId: string,
    entityId: string
  ): Promise<GenerateDisbursementDocumentsDataResponse> {
    let orderId = "";
    const invalidClaim = Object.keys(claimValues).find((key) => {
      return !validateDisbursementClaimKey(key);
    });

    // Validate claim
    if (invalidClaim) {
      throw new Error("Compose claim is not valid.");
    }

    orderId = Object.keys(claimValues)[0].split(":")[1];

    const signing = this.signing;
    // Handle get jwt token
    const token = await generateDisbursementToken(
      signing,
      providerId,
      entityId,
      claimValues,
      publicKey
    );
    const claimNames: { [key: string]: string } = {};
    Object.keys(claimValues).forEach((key) => {
      claimNames[key] = `${BNPL_ORDER_PREFIX}:${orderId}`;
      claimNames[`${key}:commitment`] = `${BNPL_ORDER_PREFIX}:${orderId}`;
    });

    const claimSourceKey = `${BNPL_ORDER_PREFIX}:${orderId}`;

    const result = {
      _claim_names: claimNames,
      _claim_sources: {
        [claimSourceKey]: {
          JWT: token,
        },
      },
    };
    return result;
  }
}
